import { type MouseEvent, useState, useCallback, memo } from 'react'
import { Box, CircularProgress, Menu, MenuItem } from '@mui/material'
import { Button } from '@ds/components'
import DoneIcon from '@mui/icons-material/Done'
import CloseIcon from '@mui/icons-material/Close'
import { APPROVAL_ANSWER_STRINGS } from '@modules/approval-answer'
import { SONG_ACTION_BUTTONS_STRINGS } from '../strings'
import { OtherReasonDialog } from './OtherReasonDialog'

interface SongActionButtonsProps {
  onApprove: () => void
  onReject: (songId: number, reason: string) => void
  songId: number
  isApproving: boolean
  isRejecting: boolean
  isLoading: boolean
  initialLoading: boolean
  isBlocked?: boolean
  processingSongId: number | null
  isCounteroffer?: boolean
  approvalDecision?: {
    requestSongId: number
    requestId: number
    isApproved: number
    rejectReason?: string
    createdAt: string
  }
}

const OTHER_REASON_KEY = 'other'

const BUTTON_STYLES = {
  px: 3,
  py: 2,
  fontSize: 16,
  minWidth: '116px',
  height: '56px',
} as const

const MENU_STYLES = {
  minWidth: 250,
  bgcolor: 'background.default',
  mt: 1,
  p: 0,
  ml: -2,
} as const

const MENU_ITEM_STYLES = {
  fontSize: 18,
  py: 2,
  px: 3,
  minHeight: 48,
} as const

const isButtonDisabled = (
  isApproving?: boolean,
  isRejecting?: boolean,
  isLoading?: boolean,
  isBlocked?: boolean,
  processingSongId?: number | null,
  currentSongId?: number,
): boolean => {
  const isCurrentSongLoading = isLoading && processingSongId === currentSongId
  return Boolean(isApproving || isRejecting || isCurrentSongLoading || isBlocked)
}

const ApproveButton = memo(
  ({
    onApprove,
    isApproving,
    isDisabled,
    isApproved,
  }: {
    onApprove: () => void
    isApproving?: boolean
    isDisabled: boolean
    isApproved?: boolean
  }) => (
    <Button
      variant={isApproved ? 'outlined' : 'contained'}
      color={isApproved ? 'success' : 'primary'}
      startIcon={isApproving ? <CircularProgress size={20} /> : <DoneIcon />}
      sx={{
        ...BUTTON_STYLES,
        ...(isApproved && {
          '&.Mui-disabled': {
            borderColor: 'success.light',
            color: 'success.light',
            backgroundColor: 'transparent',
            opacity: 0.7,
          },
        }),
      }}
      disabled={isDisabled}
      onClick={() => onApprove()}
    >
      {isApproved ? SONG_ACTION_BUTTONS_STRINGS.approved : SONG_ACTION_BUTTONS_STRINGS.approve}
    </Button>
  ),
)

const RejectButton = memo(
  ({
    onRejectClick,
    isRejecting,
    isDisabled,
    isRejected,
    isCounteroffer,
  }: {
    onRejectClick: (event: MouseEvent<HTMLButtonElement>) => void
    isRejecting?: boolean
    isDisabled: boolean
    isRejected?: boolean
    isCounteroffer?: boolean
  }) => {
    const isCounterofferState = Boolean(isRejected && isCounteroffer)
    const rejectedColor: 'error' | 'secondary' = isRejected ? 'error' : 'secondary'
    const resolvedColor = isCounterofferState ? 'info' : rejectedColor
    const rejectedLabel = isRejected
      ? SONG_ACTION_BUTTONS_STRINGS.rejected
      : SONG_ACTION_BUTTONS_STRINGS.reject
    const buttonLabel = isCounterofferState
      ? SONG_ACTION_BUTTONS_STRINGS.counteroffered
      : rejectedLabel
    const rejectIcon = isCounterofferState ? <DoneIcon /> : <CloseIcon />
    const startIcon = isRejecting ? <CircularProgress size={20} /> : rejectIcon

    return (
      <Button
        variant={isRejected || isCounterofferState ? 'outlined' : 'contained'}
        color={resolvedColor}
        startIcon={startIcon}
        sx={{
          ...BUTTON_STYLES,
          ...((isRejected || isCounterofferState) && {
            '&.Mui-disabled': {
              borderColor: isCounterofferState ? 'success.light' : 'error.main',
              color: isCounterofferState ? 'success.light' : 'error.main',
              backgroundColor: 'transparent',
              opacity: 0.7,
            },
          }),
        }}
        disabled={isDisabled}
        onClick={onRejectClick}
      >
        {buttonLabel}
      </Button>
    )
  },
)

const RejectMenu = memo(
  ({
    anchorEl,
    onClose,
    onReasonSelect,
  }: {
    anchorEl: HTMLElement | null
    onClose: () => void
    onReasonSelect: (reason: string) => void
  }) => (
    <Menu
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={onClose}
      slotProps={{
        paper: {
          sx: MENU_STYLES,
        },
      }}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'center' }}
    >
      {Object.entries(APPROVAL_ANSWER_STRINGS.rejectReasons).map(([reason, label]) => (
        <MenuItem key={reason} onClick={() => onReasonSelect(reason)} sx={MENU_ITEM_STYLES}>
          {label}
        </MenuItem>
      ))}
    </Menu>
  ),
)

export const SongActionButtons = memo(
  ({
    onApprove,
    onReject,
    songId,
    isApproving,
    isRejecting,
    isLoading,
    initialLoading,
    isBlocked,
    processingSongId,
    isCounteroffer,
    approvalDecision,
  }: SongActionButtonsProps) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
    const [otherDialogOpen, setOtherDialogOpen] = useState(false)
    const [otherReason, setOtherReason] = useState('')

    const handleRejectClick = useCallback((event: MouseEvent<HTMLButtonElement>) => {
      setAnchorEl(event.currentTarget)
    }, [])

    const handleReasonSelect = useCallback(
      (reason: string) => {
        setAnchorEl(null)
        if (reason === OTHER_REASON_KEY) {
          setOtherDialogOpen(true)
        } else {
          onReject(songId, reason)
        }
      },
      [onReject, songId],
    )

    const handleMenuClose = useCallback(() => {
      setAnchorEl(null)
    }, [])

    const handleOtherReasonChange = useCallback((value: string) => {
      setOtherReason(value)
    }, [])

    const handleOtherReasonConfirm = useCallback(() => {
      if (otherReason.trim()) {
        onReject(songId, otherReason.trim())
        setOtherDialogOpen(false)
        setOtherReason('')
      }
    }, [onReject, songId, otherReason])

    const handleOtherDialogClose = useCallback(() => {
      setOtherDialogOpen(false)
      setOtherReason('')
    }, [])

    const isDisabled =
      isButtonDisabled(isApproving, isRejecting, isLoading, isBlocked, processingSongId, songId) ||
      initialLoading

    const isApproved = approvalDecision?.isApproved === 1
    const isRejected = approvalDecision?.isApproved === 0
    const isCounterofferState = Boolean(isRejected && isCounteroffer)

    return (
      <Box display="flex" alignItems="center" gap={2} ml={2} minHeight={56}>
        {!isCounterofferState && !isRejected && (
          <ApproveButton
            onApprove={onApprove}
            isApproving={isApproving}
            isDisabled={isDisabled}
            isApproved={isApproved}
          />
        )}

        {!isApproved && (
          <>
            <RejectButton
              onRejectClick={handleRejectClick}
              isRejecting={isRejecting}
              isDisabled={isDisabled}
              isRejected={isRejected}
              isCounteroffer={isCounteroffer}
            />
            <RejectMenu
              anchorEl={anchorEl}
              onClose={handleMenuClose}
              onReasonSelect={handleReasonSelect}
            />
          </>
        )}

        <OtherReasonDialog
          open={otherDialogOpen}
          onClose={handleOtherDialogClose}
          onConfirm={handleOtherReasonConfirm}
          reason={otherReason}
          onReasonChange={handleOtherReasonChange}
        />
      </Box>
    )
  },
)
